import React, { useEffect, useState } from "react";

import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Flex,
  message,
  Popconfirm,
  Table,
  Typography,
} from "antd";

import { getErrorMessage } from "@/utilities";

import { costoEstimado, dolares, fecha, nombreDelMes } from "./agent-config";
import { agentApi, AgentFuse, AgentRunsSummary } from "./api";
import { fusibleDe } from "./asistentes";

const { Text } = Typography;

interface Props {
  storeCode: string;
  userId: number;
  editable: boolean;
}

/**
 * El consumo de un asistente (tareas 5.1 y 5.11): los turnos y el costo aproximado del mes, por
 * modelo, y su fusible con el botón para levantarlo. El tope y el fusible ya eran por usuario.
 */
export const ConsumoTab: React.FC<Props> = ({
  storeCode,
  userId,
  editable,
}) => {
  const [resumen, setResumen] = useState<AgentRunsSummary | null>(null);
  const [fuse, setFuse] = useState<AgentFuse[]>([]);
  const [cargando, setCargando] = useState(true);
  const [levantando, setLevantando] = useState(false);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    Promise.all([
      agentApi.resumen(storeCode, { agentUserId: userId }).catch(() => null),
      agentApi
        .config(storeCode, userId)
        .then((c) => c.fuse ?? [])
        .catch(() => [] as AgentFuse[]),
    ])
      .then(([r, f]) => {
        if (!vivo) return;
        setResumen(r);
        setFuse(f);
      })
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [storeCode, userId]);

  const levantarFusible = async () => {
    setLevantando(true);
    try {
      const r = await agentApi.levantarFusible(storeCode, userId);
      // Si la respuesta no trae el estado, el fusible que se levantó es el de este asistente.
      setFuse(r.fuse ?? fuse.filter((f) => Number(f.userId) !== userId));
      message.success("Fusible levantado");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setLevantando(false);
    }
  };

  const fusible = fusibleDe(fuse, userId);

  return (
    <Flex vertical gap={16}>
      {fusible && (
        <Alert
          type="error"
          showIcon
          message={`El fusible está saltado hasta ${fecha(
            fusible.disabledUntil,
          )}`}
          description={
            fusible.reason ??
            "Hizo demasiados pedidos o tuvo demasiados errores en un minuto."
          }
          action={
            editable && (
              <Popconfirm
                title="¿Levantar el fusible antes de tiempo?"
                okText="Levantar"
                cancelText="Cancelar"
                onConfirm={levantarFusible}
              >
                <Button size="small" loading={levantando}>
                  Levantar
                </Button>
              </Popconfirm>
            )
          }
        />
      )}

      <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item
          label={`Llamadas de ${
            resumen ? nombreDelMes(resumen.month) : "este mes"
          }`}
        >
          {resumen ? resumen.totals.runs.toLocaleString("es-AR") : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Fusible">
          {fusible ? "Saltado" : "Normal"}
        </Descriptions.Item>
      </Descriptions>

      {resumen && resumen.byModel.length > 0 ? (
        <>
          <Table
            size="small"
            pagination={false}
            loading={cargando}
            scroll={{ x: true }}
            rowKey={(r) => `${r.provider}/${r.model}`}
            dataSource={resumen.byModel}
            columns={[
              { title: "Modelo", render: (_, r) => r.model ?? "—" },
              {
                title: "Llamadas",
                align: "right",
                render: (_, r) => r.runs.toLocaleString("es-AR"),
              },
              {
                title: "Tokens (entrada / salida)",
                align: "right",
                render: (_, r) =>
                  `${r.tokensIn.toLocaleString(
                    "es-AR",
                  )} / ${r.tokensOut.toLocaleString("es-AR")}`,
              },
              {
                title: "Aprox. por llamada",
                align: "right",
                render: (_, r) => {
                  const c = costoEstimado(r.model, r.tokensIn, r.tokensOut);
                  return c === null || r.runs === 0 ? "—" : dolares(c / r.runs);
                },
              },
              {
                title: "Aprox. del mes",
                align: "right",
                render: (_, r) => {
                  const c = costoEstimado(r.model, r.tokensIn, r.tokensOut);
                  return c === null ? "—" : dolares(c);
                },
              },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Estimado con los precios de lista del proveedor, sin descuentos por
            caché. Es una referencia: el costo real está en la consola del
            proveedor.
          </Text>
        </>
      ) : (
        !cargando && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Todavía no respondió nada este mes"
          />
        )
      )}
    </Flex>
  );
};
